import { useCallback, useEffect, useState } from "react";
import { fetchCompanyEmployees } from "../api/companyApi";
import { enterpriseEmployees } from "../data";
import type { Employee } from "../types";
import { getAuthToken } from "../auth";

function mapDemoEmployee(employee: Employee): Employee {
  return employee;
}

function mapApiEmployee(row: {
  id: number;
  name: string;
  system_id: string;
  company_matricule: string;
  company: string;
  province: string;
  branch: string;
  status: "active" | "inactive";
  photo: string;
}): Employee {
  return {
    id: `emp-${row.id}`,
    recordId: row.id,
    photo: row.photo,
    name: row.name,
    systemId: row.system_id,
    companyMatricule: row.company_matricule,
    company: row.company,
    province: row.province || "—",
    branch: row.branch?.trim() || "Non renseignée",
    status: row.status,
  };
}

export function useEnterpriseEmployees() {
  const [employees, setEmployees] = useState<Employee[]>(() =>
    enterpriseEmployees.map(mapDemoEmployee),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);
  const [total, setTotal] = useState(enterpriseEmployees.length);

  const load = useCallback(async () => {
    if (!getAuthToken()) {
      setUsingApi(false);
      setEmployees(enterpriseEmployees.map(mapDemoEmployee));
      setTotal(enterpriseEmployees.length);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const rows = await fetchCompanyEmployees();
      const mapped = rows.map(mapApiEmployee);
      setEmployees(mapped);
      setTotal(mapped.length);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Impossible de charger les employés.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { employees, total, loading, error, usingApi, reload: load };
}
